import React, { useState, useEffect, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { usePopper } from 'react-popper';
import useConfig from '../config';
import { hasConflictWithSlots } from '../utils/time';

interface SlotSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedSlots: string[]) => void;
  referenceElement: HTMLElement | null;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'right-start' | 'right-end' | 'left-start' | 'left-end';
  slotsToOffer?: string[];
  isMorningTheory?: boolean;
  isFirstFaculty?: boolean;
  blockedSlots?: string[];
  slotConflictPairs?: string[][];
  preSelectedSlots?: string[];
}

const SlotSelector: React.FC<SlotSelectorProps> = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  referenceElement,
  placement = 'bottom',
  slotsToOffer,
  isMorningTheory = false,
  isFirstFaculty = false,
  blockedSlots = [],
  slotConflictPairs = [],
  preSelectedSlots = []
}) => {
  const config = useConfig();
  const { EXCLUDED_SLOTS, MORNING_LAB_SLOTS, EVENING_LAB_SLOTS } = config;

  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'flip', options: { padding: 8 } }
    ],
  });

  // Generate lab slots based on theory slot type
  const labSlots = useMemo(() => {
    const generatePairedSlots = (start: number, end: number) => {
      const pairs: string[] = [];
      for (let i = start; i <= end; i += 2) {
        const slot1 = `L${i}`;
        const slot2 = `L${i + 1}`;
        if (!EXCLUDED_SLOTS.includes(slot1) && !EXCLUDED_SLOTS.includes(slot2)) {
          pairs.push(`${slot1}+${slot2}`);
        }
      }
      return pairs;
    };

    if (isMorningTheory) {
      // L31-L60 for morning theory
      return generatePairedSlots(31, 60);
    } else {
      // L1-L30 for evening theory
      return generatePairedSlots(1, 30);
    }
  }, [isMorningTheory, EXCLUDED_SLOTS]);

  // Initialize with preSelectedSlots when the component opens
  useEffect(() => {
    if (isOpen && preSelectedSlots.length > 0) {
      const selectedPairs = labSlots.filter(pair => {
        const [slot1, slot2] = pair.split('+');
        return preSelectedSlots.includes(slot1) && preSelectedSlots.includes(slot2);
      });
      setSelectedSlots(selectedPairs);
    } else if (!isOpen) {
      setSelectedSlots([]); // Reset when closing
    }
  }, [isOpen, preSelectedSlots, labSlots]);

  const isSlotBlocked = (slot: string) => {
    if (!isFirstFaculty) return false;

    const [slot1, slot2] = slot.split('+');
    
    // Check if slots are directly blocked
    if (blockedSlots.includes(slot1) || blockedSlots.includes(slot2)) return true;

    // Check for time-based conflicts with blocked slots
    return hasConflictWithSlots(slot1, blockedSlots, config) || 
           hasConflictWithSlots(slot2, blockedSlots, config);
  };

  const toggleSlot = (slot: string) => {
    if (isSlotBlocked(slot)) return;

    setSelectedSlots(prev => 
      prev.includes(slot) 
        ? prev.filter(s => s !== slot)
        : [...prev, slot]
    );
  };

  const getAvailableSlots = () => {
    if (slotsToOffer) {
      return slotsToOffer;
    }
    return isMorningTheory ? EVENING_LAB_SLOTS : MORNING_LAB_SLOTS;
  };

  if (!isOpen) return null;

  const availableSlots = getAvailableSlots();

  const handleSelect = (slot: string) => {
    onSelect([slot]);
  };

  return (
    <>
      <div
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        className="bg-white rounded-xl shadow-xl w-[300px] max-w-[95vw] z-[70]"
      >
        <div ref={setArrowElement} style={styles.arrow} className="popper-arrow" />
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 break-words">Select Lab Slots</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <IoClose size={20} />
            </button>
          </div>

          {/* Lab Slots Grid */}
          <div className="grid grid-cols-3 gap-2">
            {availableSlots.map(slot => {
              const isCurrentlyBlocked = isSlotBlocked(slot);
              let buttonClasses = 'py-2 px-3 rounded-lg text-sm font-medium transition-all';
              let title = '';

              if (selectedSlots.includes(slot)) {
                buttonClasses += ' bg-black text-white';
              } else if (isCurrentlyBlocked) { // This implies isFirstFaculty
                buttonClasses += ' bg-gray-100 text-gray-400 cursor-not-allowed opacity-70'; // Neutral disabled
                title = 'This slot is not available';
              } else {
                buttonClasses += ' bg-gray-100 text-gray-700 hover:bg-gray-200'; // Default available
              }

              return (
                <button
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  disabled={isCurrentlyBlocked} // Disable if blocked (only happens for first faculty due to isSlotBlocked logic)
                  className={buttonClasses}
                  title={title}
                >
                  {slot}
                </button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const individualSlots = selectedSlots.flatMap(s => s.split('+'));
                onSelect(individualSlots);
                onClose();
              }}
              disabled={selectedSlots.length === 0}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors
                ${selectedSlots.length > 0
                  ? 'bg-black' // Enabled: black background, no hover
                  : 'bg-gray-300 cursor-not-allowed' // Disabled: gray background
                }
              `}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlotSelector; 